import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data/content";
import { useScrollTo } from "./scroll";
import { EASE } from "./primitives";
import { openDemoChooser } from "./DemoBookingModal";

const Wordmark = ({ onClick }) => (
  <button
    onClick={onClick}
    data-testid="brand-wordmark"
    className="group flex items-center"
    aria-label="AJMUS home"
  >
    <img
      src="/brand/ajmus-logo.png"
      alt="AJMUS"
      className="h-10 w-auto max-w-[154px] object-contain transition-transform duration-300 group-hover:scale-[1.02] sm:h-11"
    />
  </button>
);

export default function Header() {
  const scrollToId = useScrollTo();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.id);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "border-b border-black/5 bg-white/80 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Wordmark onClick={() => go("home")} />

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-${l.id}`}
              className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                active === l.id
                  ? "text-ajmus-navy"
                  : "text-ajmus-steel hover:text-ajmus-ink"
              }`}
            >
              {l.label}
              {active === l.id && (
                <motion.span
                  layoutId="nav-active"
                  className="absolute inset-0 -z-10 rounded-full bg-ajmus-navy/5 ring-1 ring-ajmus-navy/10"
                  transition={{ duration: 0.3, ease: EASE }}
                />
              )}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex">
          <button
            onClick={() => go("contact")}
            data-testid="header-talk-btn"
            className="rounded-full px-4 py-2.5 text-sm font-semibold text-ajmus-navy transition-colors duration-200 hover:bg-ajmus-navy/5"
          >
            Talk to Us
          </button>
          <button
            onClick={openDemoChooser}
            data-testid="header-demo-btn"
            className="rounded-full bg-ajmus-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-black"
          >
            Live Demo
          </button>
        </div>

        <button
          className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-black/10 bg-white/70 text-ajmus-ink lg:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden border-t border-black/5 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-nav-${l.id}`}
                  className={`block w-full rounded-xl px-4 py-3 text-left text-base font-medium transition-colors ${
                    active === l.id ? "bg-ajmus-navy/5 text-ajmus-navy" : "text-ajmus-steel"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <div className="grid grid-cols-2 gap-2.5 pt-3">
                <button
                  onClick={() => go("contact")}
                  data-testid="mobile-talk-btn"
                  className="rounded-full border border-ajmus-navy/15 px-4 py-3 text-sm font-semibold text-ajmus-navy"
                >
                  Talk to Us
                </button>
                <button
                  onClick={() => { setOpen(false); openDemoChooser(); }}
                  data-testid="mobile-demo-btn"
                  className="rounded-full bg-ajmus-navy px-4 py-3 text-sm font-semibold text-white"
                >
                  Live Demo
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
